/**
 * LanguageSelector - Language picker dropdown for CodeNode
 *
 * A dropdown component that allows users to switch between Python and JavaScript.
 * Shows a language icon and name, with a dropdown for selection.
 *
 * Design follows the same patterns as GeneratorNode's model selector:
 * - Clean dropdown with gray background
 * - Focus states with purple accent
 * - Icons/emojis for visual identification
 */

import { useCallback } from 'react';
import { CODE_LANGUAGES, type CodeLanguage } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

export interface LanguageSelectorProps {
  /**
   * Currently selected language
   */
  language: CodeLanguage;

  /**
   * Callback when language selection changes
   * @param language - New language selection
   */
  onLanguageChange: (language: CodeLanguage) => void;

  /**
   * Whether the selector is disabled
   * @default false
   */
  disabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * LanguageSelector Component
 *
 * A dropdown for selecting the programming language in a CodeNode.
 * Displays the language icon and name, with options for both Python and JavaScript.
 *
 * The component uses nodrag/nopan classes to prevent React Flow from
 * capturing mouse events when interacting with the dropdown.
 */
export function LanguageSelector({
  language,
  onLanguageChange,
  disabled = false,
}: LanguageSelectorProps) {
  // ==================== HANDLERS ====================

  /**
   * Handle dropdown selection change
   * Validates the selection is a valid CodeLanguage before calling callback
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newLanguage = event.target.value as CodeLanguage;
      // Validate it's a known language
      if (CODE_LANGUAGES.some((lang) => lang.id === newLanguage)) {
        onLanguageChange(newLanguage);
      }
    },
    [onLanguageChange]
  );

  // ==================== RENDER ====================

  // Get the current language info for display
  const currentLang = CODE_LANGUAGES.find((l) => l.id === language) || CODE_LANGUAGES[0];

  return (
    <div className="nodrag nopan">
      {/* Label */}
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Language
      </label>

      {/* Dropdown select */}
      <select
        value={language}
        onChange={handleChange}
        disabled={disabled}
        className="
          w-full px-3 py-2 text-[14px] font-medium
          bg-gray-50 border border-gray-200 rounded-lg
          outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-100
          transition-all cursor-pointer
          text-black
          disabled:opacity-50 disabled:cursor-not-allowed
        "
        onClick={(e) => e.stopPropagation()}
      >
        {CODE_LANGUAGES.map((lang) => (
          <option key={lang.id} value={lang.id}>
            {lang.icon} {lang.name}
          </option>
        ))}
      </select>

      {/* File extension hint */}
      <p className="mt-1 text-[10px] text-gray-400">
        File extension: {currentLang.fileExtension}
      </p>
    </div>
  );
}

// Export as default as well for convenience
export default LanguageSelector;
